import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { del, get, post } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const KEY = "prodtrack_alertes_acquittees";
const read = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; } };

export const interventionKey = (r) => `${r.ligne}|${r.etat}|${r.cause || ""}`;

/** Acquittements partagés (serveur, /ext/acquittements). Les comptes démo (lecture seule) retombent sur le navigateur. */
export const useAcquittements = () => {
  const { isDemo, entrepriseId, user } = useAuth();
  const qc = useQueryClient();
  const [local, setLocal] = useState(read);
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(local)); }, [local]);
  const ready = user.role !== "superadmin" || !!entrepriseId;
  const srv = useQuery({ queryKey: ["acquittements", entrepriseId], queryFn: () => get("/ext/acquittements"), enabled: ready && !isDemo, refetchInterval: 10000 });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["acquittements"] });
  const onError = (e) => toast.error(e.message);
  const add = useMutation({ mutationFn: ({ cle, note }) => post("/ext/acquittements", { cle, note }), onSuccess: invalidate, onError });
  const rm = useMutation({ mutationFn: (cle) => del(`/ext/acquittements?cle=${encodeURIComponent(cle)}`), onSuccess: invalidate, onError });
  const purge = useMutation({ mutationFn: () => del("/ext/acquittements"), onSuccess: invalidate, onError });

  if (isDemo) {
    return {
      acks: local, mode: "local",
      acquitter: (r, note = "") => setLocal((a) => ({ ...a, [interventionKey(r)]: { at: new Date().toISOString(), par: user?.nom || "—", note } })),
      retirer: (r) => setLocal((a) => { const n = { ...a }; delete n[interventionKey(r)]; return n; }),
      purger: () => setLocal({}),
    };
  }
  const acks = Object.fromEntries((srv.data || []).map((a) => [a.cle, { at: a.at, par: a.par, note: a.note }]));
  return { acks, mode: "serveur", acquitter: (r, note = "") => add.mutate({ cle: interventionKey(r), note }), retirer: (r) => rm.mutate(interventionKey(r)), purger: () => purge.mutate() };
};

export const SEUILS = { trs: 60, performance: 70, qualite: 95 };

/** Historique reconstitué à partir des tendances journalières (seuils identiques au backend). */
export const historiqueDepuisTendances = (parLigne) => {
  const out = [];
  parLigne.forEach(({ ligne, points }) => (points || []).forEach((p) => {
    if (p.trs == null) return;
    if (p.trs < SEUILS.trs) out.push({ date: p.date, ligne, niveau: "rouge", type: "TRS", texte: `TRS à ${p.trs} % (< ${SEUILS.trs} %)`, valeur: p.trs });
    if (p.performance != null && p.performance < SEUILS.performance) out.push({ date: p.date, ligne, niveau: "orange", type: "Performance", texte: `Performance à ${p.performance} % (< ${SEUILS.performance} %)`, valeur: p.performance });
    if (p.qualite != null && p.qualite < SEUILS.qualite) out.push({ date: p.date, ligne, niveau: "orange", type: "Qualité", texte: `Qualité à ${p.qualite} % (< ${SEUILS.qualite} %)`, valeur: p.qualite });
  }));
  return out.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : a.niveau === "rouge" ? -1 : 1));
};
